// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { FileText } from 'lucide-react'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { YesNoStep } from './YesNoStep'
import { Input }     from '@/components/ui/Input'
import { AddressFields }   from '@/components/ui/AddressFields'
import { SelectWithOther } from '@/components/ui/SelectWithOther'
import { NOTARY_OFFICES, findOffice } from '@/lib/constants/offices'
import { Card }      from '@/components/ui/Card'
import { useWizardStore, useAuthStore } from '@/store'
import { loadChildren, loadTestamentDetails, saveTestamentDetails, EMPTY_TESTAMENT_DETAILS, type TestamentDetails } from '@/services/wizard'
import { saveDocument } from '@/services/wishes'
import { useState, useEffect, useCallback } from 'react'
import { useAutosave } from '@/hooks/useAutosave'
import type { SaveStatus } from '@/hooks/useAutosave'

export function NewStep09Testament({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { answers, setAnswer } = useWizardStore()
  const { userId } = useAuthStore()
  const [hasMinors,  setHasMinors]  = useState(false)
  const [details,    setDetails]    = useState<TestamentDetails>(EMPTY_TESTAMENT_DETAILS)
  const [ready,      setReady]      = useState(false)
  const hasTestament = answers.hasTestament

  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  useEffect(() => {
    if (!userId) return
    loadChildren(userId).then(d => {
      if (!d?.children?.length) return
      const now = Date.now()
      setHasMinors(d.children.some(ch => {
        try {
          const age = Math.floor((now - new Date(ch.dob).getTime()) / (365.25*24*3600*1000))
          return age >= 0 && age < 18
        } catch { return false }
      }))
    })
    loadTestamentDetails(userId).then(d => {
      if (d) setDetails(d)
      setReady(true)
    })
  }, [userId])

  const save = useCallback(async (d: TestamentDetails) => {
    if (!userId) return
    await saveTestamentDetails(userId, d)
    // Documentkluis koppeling: als documentLocation is ingevuld, sla op in document_index
    if (d.documentLocation.trim()) {
      await saveDocument(userId, {
        category:   'LEGAL',
        label:      'Testament',
        location:   d.documentLocation,
        notes:      d.notarisName ? `Notaris: ${d.notarisName}${d.notarisCity ? ', ' + d.notarisCity : ''}` : '',
        isVerified: false,
      })
    }
  }, [userId])

  const { status } = useAutosave({ data: details, onSave: save, enabled: ready && hasTestament === true })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function handleChange(v: string) {
    setAnswer('hasTestament', v === 'yes')
    onSaveStatus('saved')
    setTimeout(() => onSaveStatus('idle'), 1500)
  }

  const value = hasTestament === true ? 'yes' : hasTestament === false ? 'no' : ''

  return (
    <div className="flex flex-col gap-6">

      {/* Voogdijwaarschuwing bij minderjarige kinderen */}
      {hasMinors && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
          <span className="text-base flex-shrink-0">⚠️</span>
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>U heeft minderjarige kinderen.</strong> Controleer of voogdij in uw testament is geregeld.
            Zonder testamentaire voogdijregeling beslist de rechter wie de voogd wordt.
          </p>
        </div>
      )}

      {/* Goed om te weten VOOR de vraag */}
      <InfoBlock title="Goed om te weten - testament" variant="blue" defaultOpen={false}>
        <p>Het gaat hier niet om de inhoud van uw testament; die kent de notaris al. Het gaat erom dat uw familie weet dát er een is, en waar het ligt. Vergeet ook niet erbij te zetten wie u tot executeur heeft benoemd; dat is degene die de erfenis mag afhandelen.</p>
        <p>Weet niemand het, dan kan elke notaris via het <strong>Centraal Testamentenregister (CTR)</strong> opzoeken of er een testament is. Toch scheelt het uw familie dagen als u het hier gewoon noteert.</p>
        <p>Heeft u geen testament, dan geldt het wettelijk erfrecht en gaan uw bezittingen naar uw partner en kinderen. Goed om uw testament te bekijken na een huwelijk, echtscheiding, de geboorte van een kind of een grote verandering in uw vermogen.</p>
                                <p className="mt-2 flex flex-col gap-1">
          <a href="https://www.notaris.nl/notariele-producten/testament" target="_blank" rel="noopener noreferrer"
             className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
            Notaris.nl - meer over testamenten en hoe u er een laat opstellen →
          </a>
          <a href="https://www.belastingdienst.nl/wps/wcm/connect/nl/erfbelasting/content/erfbelasting_wanneer_doe_ik_aangifte" target="_blank" rel="noopener noreferrer"
             className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
            Belastingdienst.nl - aangifte erfbelasting →
          </a>
        </p>
        <p className="text-xs text-ink-4 mt-2 pt-2 border-t border-sand-200 leading-relaxed">
          WilsApp legt uw wensen vast als persoonlijke leidraad. Voor officiële registratie,
          juridische geldigheid of medische besluitvorming verwijzen wij naar de bevoegde instantie.
        </p>
      </InfoBlock>

      <YesNoStep
        title="Testament"
        question="Heeft u een testament?"
        description="Een testament legt uw laatste wilsbeschikking vast bij de notaris."
        value={value}
        onChange={handleChange}
        conditionKey="yes"
        conditionContent={
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3 bg-white rounded-2xl border border-sage-200 p-4">
              <FileText className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink mb-1">Notarisgegevens</p>
                <p className="text-xs text-ink-3">Noteer bij welke notaris uw testament ligt, zodat uw naasten het snel kunnen vinden.</p>
              </div>
            </div>
            <Card>
              <div className="flex flex-col gap-3">
                <div>
                  <Input label="Notariskantoor" list="notary-office-list" value={details.notarisName}
                    onChange={e => {
                      const name = e.target.value
                      const o = findOffice(NOTARY_OFFICES, name)
                      setDetails(p => o ? {
                        ...p,
                        notarisName:       o.name,
                        notarisStraat:     o.street ?? p.notarisStraat,
                        notarisHuisnummer: o.houseNumber ?? p.notarisHuisnummer,
                        notarisPostcode:   o.postalCode ?? p.notarisPostcode,
                        notarisCity:       o.city ?? p.notarisCity,
                        notarisPhone:      o.phone ?? p.notarisPhone,
                        notarisEmail:      o.email ?? p.notarisEmail,
                        notarisWebsite:    o.website ?? p.notarisWebsite,
                      } : { ...p, notarisName: name })
                    }}
                    placeholder="Zoek of typ de naam van het kantoor..." />
                  <datalist id="notary-office-list">
                    {NOTARY_OFFICES.map(o => <option key={o.name} value={o.name} />)}
                  </datalist>
                </div>
                <AddressFields
                  showAddition={false}
                  values={{
                    postalCode:  details.notarisPostcode ?? '',
                    houseNumber: details.notarisHuisnummer ?? '',
                    street:      details.notarisStraat ?? '',
                    city:        details.notarisCity,
                  }}
                  onChange={(field, val) => {
                    const map: Partial<Record<string, keyof TestamentDetails>> = {
                      postalCode: 'notarisPostcode', houseNumber: 'notarisHuisnummer', street: 'notarisStraat', city: 'notarisCity',
                    }
                    const key = map[field]
                    if (key) setDetails(p => ({ ...p, [key]: val }))
                  }}
                  labels={{ street: 'Straat', houseNumber: 'Huisnummer', city: 'Plaats' }}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Telefoonnummer" type="tel" value={details.notarisPhone ?? ''}
                    onChange={e => setDetails(p => ({ ...p, notarisPhone: e.target.value }))}
                    placeholder="Bijv: 072 123 45 67" />
                  <Input label="E-mailadres" type="email" value={details.notarisEmail ?? ''}
                    onChange={e => setDetails(p => ({ ...p, notarisEmail: e.target.value }))}
                    placeholder="Bijv: info@notariskantoor.nl" />
                </div>
                <Input label="Website" type="url" value={details.notarisWebsite ?? ''}
                  onChange={e => setDetails(p => ({ ...p, notarisWebsite: e.target.value }))}
                  placeholder="Bijv: www.notariskantoor.nl" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Jaar van opstellen" value={details.year}
                    onChange={e => setDetails(p => ({ ...p, year: e.target.value }))}
                    placeholder="Bijv: 2019" />
                  <SelectWithOther
                    label="Locatie document"
                    options={['Thuis', 'Kluis', 'Bankkluis', 'Bij notaris', 'Digitaal opgeslagen', 'Vertrouwenspersoon']}
                    value={details.documentLocation}
                    onChange={val => setDetails(p => ({ ...p, documentLocation: val }))}
                    placeholder="Kies locatie..."
                    otherOptionLabel="Elders"
                    otherFieldLabel="Locatie (anders)"
                    otherPlaceholder="Bijv: bij mijn dochter"
                  />
                </div>
                {details.documentLocation && (
                  <p className="text-xs text-sage-600">
                    ✓ Locatie wordt ook opgeslagen in uw documentwijzer.
                  </p>
                )}
              </div>
            </Card>
          </div>
        }
      />

      <InfoBlock title="Tip van WilsApp" variant="info" defaultOpen={true}>
        <p>Controleer eens in de paar jaar of uw testament nog aansluit bij uw huidige situatie. Bij veranderingen zoals een scheiding, nieuw huwelijk, overlijden van een erfgenaam of gewijzigde familieomstandigheden kan het verstandig zijn uw testament opnieuw te bespreken met uw notaris.</p>
      </InfoBlock>

      <InfoBlock title="Wat kunt u vastleggen in een testament?" defaultOpen={false}>
        <p><strong>Erfgenamen</strong> - wie erft uw bezittingen?</p>
        <p><strong>Executeur</strong> - wie wikkelt uw nalatenschap af?</p>
        <p><strong>Voogdij</strong> - wie krijgt de voogdij over uw minderjarige kinderen?</p>
        <p><strong>Legaten</strong> - specifieke bezittingen aan specifieke personen.</p>
      </InfoBlock>
    </div>
  )
}
